/**
 * ConnectionStatus - Modal overlay for connection problems
 *
 * Per CONTEXT.md decisions:
 * - Only show problems (no indicator when connected)
 * - Modal blocking overlay when disconnected
 * - Read-only mode if reconnection completely fails
 * - Auto-retry first, then "Try again" button
 */

import type { ConnectionStatus as ConnectionStatusType } from '@deep-agents/shared-types';

interface ConnectionStatusProps {
  // Current connection status
  status: ConnectionStatusType;
  // Manual reconnect trigger
  onReconnect: () => void;
}

export function ConnectionStatus({ status, onReconnect }: ConnectionStatusProps) {
  // Per CONTEXT.md: Only show problems, no indicator when connected
  if (status === 'connected' || status === 'connecting') {
    return null;
  }

  // Disconnected - show modal with retry option
  if (status === 'disconnected') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="mx-4 max-w-sm rounded-lg bg-white p-6 shadow-xl">
          {/* Icon */}
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
            <svg
              className="h-6 w-6 text-yellow-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>

          <h2 className="mb-2 text-lg font-semibold text-gray-900">
            Connection Lost
          </h2>

          <p className="mb-4 text-sm text-gray-600">
            We're trying to reconnect you to the board. Your changes are saved
            locally and will sync when the connection is restored.
          </p>

          {/* Reconnecting indicator */}
          <div className="mb-4 flex items-center gap-2 text-sm text-gray-500">
            <svg
              className="h-4 w-4 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>Reconnecting...</span>
          </div>

          <button
            onClick={onReconnect}
            className="w-full rounded-lg bg-blue-600 px-4 py-2 font-medium text-white transition-colors hover:bg-blue-700"
          >
            Try Again Now
          </button>
        </div>
      </div>
    );
  }

  // Read-only mode - reconnection failed
  if (status === 'readonly') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="mx-4 max-w-sm rounded-lg bg-white p-6 shadow-xl">
          {/* Icon */}
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <svg
              className="h-6 w-6 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
              />
            </svg>
          </div>

          <h2 className="mb-2 text-lg font-semibold text-gray-900">
            Read-Only Mode
          </h2>

          <p className="mb-4 text-sm text-gray-600">
            Unable to reconnect to the server. You can view the board but
            changes won't be saved. Refresh the page to try again.
          </p>

          <button
            onClick={() => window.location.reload()}
            className="w-full rounded-lg bg-gray-900 px-4 py-2 font-medium text-white transition-colors hover:bg-gray-800"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return null;
}
